import apiClient from './config';

// ============================================
// AUTHENTICATION ENDPOINTS
// ============================================
export const authAPI = {
  // Health Check
  healthCheck: () => apiClient.get('/health'),
  
  // Login
  login: (credentials) => apiClient.post('/login', credentials),
  
  // Register
  register: (userData) => apiClient.post('/register', userData),
  
  // Logout
  logout: () => apiClient.post('/logout'),
  
  // Verify User (Get Current User)
  verify: () => apiClient.get('/verify'),
  
  // Password Reset Request
  passwordResetRequest: (email) => apiClient.post('/password-reset/request', { email }),
  
  // Password Reset Confirmation
  passwordResetConfirm: (email, resetToken, newPassword) => 
    apiClient.post('/password-reset/confirm', { email, reset_token: resetToken, new_password: newPassword }),
};

// ============================================
// USER ENDPOINTS
// ============================================
export const userAPI = {
  // Get user profile
  getProfile: (userId) => apiClient.get(`/users/${userId}`),
  
  // Update user profile
  updateProfile: (userId, data) => apiClient.put(`/users/${userId}`, data),
  
  // Upload profile picture
  uploadAvatar: (userId, formData) => apiClient.post(`/users/${userId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // Delete user
  deleteUser: (userId) => apiClient.delete(`/users/${userId}`),
  
  // Get all users (Admin only)
  getAllUsers: (params) => apiClient.get('/users', { params }),
};

// ============================================
// COURSE ENDPOINTS
// ============================================
export const courseAPI = {
  // Get all courses
  getAllCourses: (params) => apiClient.get('/courses', { params }),
  
  // Get course by ID
  getCourseById: (courseId) => apiClient.get(`/courses/${courseId}`),
  
  // Create course (Teacher only)
  createCourse: (courseData) => apiClient.post('/courses', courseData),
  
  // Update course (Teacher only)
  updateCourse: (courseId, courseData) => apiClient.put(`/courses/${courseId}`, courseData),
  
  // Delete course (Teacher only)
  deleteCourse: (courseId) => apiClient.delete(`/courses/${courseId}`),
  
  // Get courses by teacher
  getCoursesByTeacher: (teacherId) => apiClient.get(`/courses/teacher/${teacherId}`),
  
  // Get enrolled courses (Student)
  getEnrolledCourses: (studentId) => apiClient.get(`/courses/enrolled/${studentId}`),
  
  // Enroll in course
  enrollCourse: (courseId) => apiClient.post(`/courses/${courseId}/enroll`),
  
  // Unenroll from course
  unenrollCourse: (courseId) => apiClient.post(`/courses/${courseId}/unenroll`),
  
  // Upload course thumbnail
  uploadThumbnail: (courseId, formData) => apiClient.post(`/courses/${courseId}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// ============================================
// VIDEO/LESSON ENDPOINTS
// ============================================
export const lessonAPI = {
  // Get lessons by course
  getLessonsByCourse: (courseId) => apiClient.get(`/courses/${courseId}/lessons`),
  
  // Get lesson by ID
  getLessonById: (lessonId) => apiClient.get(`/lessons/${lessonId}`),
  
  // Create lesson (Teacher only)
  createLesson: (courseId, lessonData) => apiClient.post(`/courses/${courseId}/lessons`, lessonData),
  
  // Update lesson (Teacher only)
  updateLesson: (lessonId, lessonData) => apiClient.put(`/lessons/${lessonId}`, lessonData),
  
  // Delete lesson (Teacher only)
  deleteLesson: (lessonId) => apiClient.delete(`/lessons/${lessonId}`),
  
  // Upload video
  uploadVideo: (lessonId, formData, onUploadProgress) => apiClient.post(
    `/lessons/${lessonId}/video`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }
  ),
  
  // Mark lesson as completed
  markAsCompleted: (lessonId) => apiClient.post(`/lessons/${lessonId}/complete`),
  
  // Get lesson progress
  getLessonProgress: (lessonId, studentId) => apiClient.get(`/lessons/${lessonId}/progress/${studentId}`),
};

// ============================================
// PAYMENT/SUBSCRIPTION ENDPOINTS
// ============================================
export const paymentAPI = {
  // Get subscription plans
  getPlans: () => apiClient.get('/payments/plans'),
  
  // Create subscription
  createSubscription: (planId, paymentData) => apiClient.post('/payments/subscribe', { planId, ...paymentData }),
  
  // Cancel subscription
  cancelSubscription: (subscriptionId) => apiClient.post(`/payments/subscriptions/${subscriptionId}/cancel`),
  
  // Get payment history
  getPaymentHistory: (userId) => apiClient.get(`/payments/history/${userId}`),
  
  // Process payment
  processPayment: (paymentData) => apiClient.post('/payments/process', paymentData),
  
  // Verify payment (M-Pesa, etc.)
  verifyPayment: (transactionId) => apiClient.post('/payments/verify', { transactionId }),
};

// ============================================
// ANALYTICS/DASHBOARD ENDPOINTS
// ============================================
export const analyticsAPI = {
  // Get student analytics
  getStudentAnalytics: (studentId) => apiClient.get(`/analytics/student/${studentId}`),
  
  // Get teacher analytics
  getTeacherAnalytics: (teacherId) => apiClient.get(`/analytics/teacher/${teacherId}`),
  
  // Get parent analytics
  getParentAnalytics: (parentId) => apiClient.get(`/analytics/parent/${parentId}`),
  
  // Get admin analytics
  getAdminAnalytics: () => apiClient.get('/analytics/admin'),
  
  // Get system owner analytics
  getSystemOwnerAnalytics: () => apiClient.get('/analytics/system-owner'),
};

// ============================================
// NOTIFICATION ENDPOINTS
// ============================================
export const notificationAPI = {
  // Get all notifications
  getNotifications: (userId) => apiClient.get(`/notifications/${userId}`),
  
  // Mark notification as read
  markAsRead: (notificationId) => apiClient.put(`/notifications/${notificationId}/read`),
  
  // Mark all as read
  markAllAsRead: (userId) => apiClient.put(`/notifications/${userId}/read-all`),
  
  // Delete notification
  deleteNotification: (notificationId) => apiClient.delete(`/notifications/${notificationId}`),
};

// ============================================
// PARENT-STUDENT RELATIONSHIP ENDPOINTS
// ============================================
export const parentAPI = {
  // Link student to parent
  linkStudent: (parentId, studentId) => apiClient.post('/parents/link-student', { parentId, studentId }),
  
  // Unlink student from parent
  unlinkStudent: (parentId, studentId) => apiClient.post('/parents/unlink-student', { parentId, studentId }),
  
  // Get parent's children
  getChildren: (parentId) => apiClient.get(`/parents/${parentId}/children`),
  
  // Get child's progress
  getChildProgress: (parentId, studentId) => apiClient.get(`/parents/${parentId}/children/${studentId}/progress`),
};

// ============================================
// SEARCH ENDPOINTS
// ============================================
export const searchAPI = {
  // Search courses
  searchCourses: (query, filters) => apiClient.get('/search/courses', { params: { query, ...filters } }),
  
  // Search teachers
  searchTeachers: (query) => apiClient.get('/search/teachers', { params: { query } }),
  
  // Get popular courses
  getPopularCourses: () => apiClient.get('/search/popular-courses'),
  
  // Get recommended courses
  getRecommendedCourses: (userId) => apiClient.get(`/search/recommended/${userId}`),
};

